/**
 * Upload Store
 * Tracks pending image uploads to ensure canvas is only saved
 * after all uploads are complete (avoiding blob:// URL issues)
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { logger } from '@/utils/logger';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

export interface PendingUpload {
  /** Temporary ID for the Fabric object */
  fabricObjectId: string;
  /** Temporary blob URL (will be revoked after upload) */
  blobUrl: string;
  /** Upload status */
  status: 'uploading' | 'completed' | 'failed';
  /** Permanent URL after upload completes */
  permanentUrl?: string;
  /** Error message if upload failed */
  error?: string;
  /** Timestamp when upload started */
  startedAt: number;
}

interface UploadState {
  /** Map of pending uploads by fabricObjectId */
  pending: Map<string, PendingUpload>;

  // Actions
  /** Register a new upload */
  startUpload: (fabricObjectId: string, blobUrl: string) => void;

  /** Mark upload as completed */
  completeUpload: (fabricObjectId: string, permanentUrl: string) => void;

  /** Mark upload as failed */
  failUpload: (fabricObjectId: string, error: string) => void;

  /** Remove upload from tracking */
  removeUpload: (fabricObjectId: string) => void;

  /** Clear all uploads */
  clearAll: () => void;

  // Queries
  /** Check if there are any pending uploads */
  hasPendingUploads: () => boolean;

  /** Get count of pending uploads */
  getPendingCount: () => number;

  /** Wait for all pending uploads to complete */
  waitForAllUploads: (timeoutMs?: number) => Promise<void>;

  /** Get all blob URLs that are still pending */
  getPendingBlobUrls: () => string[];
}

// ═══════════════════════════════════════════════════════════════════
// STORE
// ═══════════════════════════════════════════════════════════════════

export const useUploadStore = create<UploadState>()(
  devtools(
    (set, get) => ({
      pending: new Map(),

      startUpload: (fabricObjectId, blobUrl) => {
        set((state) => {
          const newPending = new Map(state.pending);
          newPending.set(fabricObjectId, {
            fabricObjectId,
            blobUrl,
            status: 'uploading',
            startedAt: Date.now(),
          });
          return { pending: newPending };
        });
        logger.debug('UploadStore', `Started upload for: ${fabricObjectId}`);
      },

      completeUpload: (fabricObjectId, permanentUrl) => {
        set((state) => {
          const newPending = new Map(state.pending);
          const upload = newPending.get(fabricObjectId);
          if (upload) {
            newPending.set(fabricObjectId, {
              ...upload,
              status: 'completed',
              permanentUrl,
            });
          }
          return { pending: newPending };
        });
        logger.debug('UploadStore', `Completed upload: ${fabricObjectId}`);
      },

      failUpload: (fabricObjectId, error) => {
        set((state) => {
          const newPending = new Map(state.pending);
          const upload = newPending.get(fabricObjectId);
          if (upload) {
            newPending.set(fabricObjectId, {
              ...upload,
              status: 'failed',
              error,
            });
          }
          return { pending: newPending };
        });
        logger.warn(
          'UploadStore',
          `Failed upload for: ${fabricObjectId} - ${error}`
        );
      },

      removeUpload: (fabricObjectId) => {
        set((state) => {
          const newPending = new Map(state.pending);
          newPending.delete(fabricObjectId);
          return { pending: newPending };
        });
      },

      clearAll: () => {
        set({ pending: new Map() });
      },

      hasPendingUploads: () => {
        const { pending } = get();
        for (const upload of pending.values()) {
          if (upload.status === 'uploading') {
            return true;
          }
        }
        return false;
      },

      getPendingCount: () => {
        const { pending } = get();
        let count = 0;
        for (const upload of pending.values()) {
          if (upload.status === 'uploading') {
            count++;
          }
        }
        return count;
      },

      waitForAllUploads: async (timeoutMs = 30000) => {
        const startTime = Date.now();
        const pollInterval = 100; // Check every 100ms

        return new Promise<void>((resolve, reject) => {
          const checkUploads = () => {
            // Check timeout
            if (Date.now() - startTime > timeoutMs) {
              reject(new Error('Upload timeout exceeded'));
              return;
            }

            // Check if all uploads are done
            if (!get().hasPendingUploads()) {
              logger.debug('UploadStore', 'All uploads completed');
              resolve();
              return;
            }

            // Continue polling
            setTimeout(checkUploads, pollInterval);
          };

          checkUploads();
        });
      },

      getPendingBlobUrls: () => {
        const { pending } = get();
        const urls: string[] = [];
        for (const upload of pending.values()) {
          if (upload.status === 'uploading') {
            urls.push(upload.blobUrl);
          }
        }
        return urls;
      },
    }),
    { name: 'UploadStore' }
  )
);

// ═══════════════════════════════════════════════════════════════════
// SELECTORS
// ═══════════════════════════════════════════════════════════════════

export const selectHasPendingUploads = (state: UploadState) =>
  state.hasPendingUploads();

export const selectPendingCount = (state: UploadState) =>
  state.getPendingCount();
